'use client';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { login, register, forgotPassword } from '@/lib/auth';

// Tela de login da aplicação. O Keycloak NÃO é aberto: o formulário manda
// email+senha para a nossa API, que troca por tokens.
export function LoginScreen({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [tab, setTab] = useState<'login' | 'register' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const switchTab = (next: 'login' | 'register') => {
    setTab(next);
    setError(null);
    setInfo(null);
    setPassword('');
    setConfirm('');
  };

  const openForgotPassword = () => {
    setTab('forgot');
    setError(null);
    setInfo(null);
    setForgotSent(false);
  };

  async function handleForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email.trim());
      // Mesma mensagem exista ou não o email: a API nunca revela isso, e a tela
      // também não deve — senão a enumeração de contas só mudaria de lugar.
      setForgotSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (tab === 'register' && password !== confirm) {
      setError('As senhas não conferem.');
      return;
    }

    setBusy(true);
    try {
      if (tab === 'login') {
        await login(email.trim(), password);
        onAuthenticated();
      } else {
        await register(email.trim(), password);
        // Cadastro não autentica: o usuário confirma a senha entrando.
        setTab('login');
        setConfirm('');
        setInfo('Conta criada. Entre com seu email e senha.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        // A cor fica como fallback: é o que aparece enquanto a foto carrega.
        bgcolor: 'background.default',
        backgroundImage: 'url(/castanha-login.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Card elevation={8} sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} align="center">
            Castanha do Pará
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Mutran Exportadora
          </Typography>

          {tab !== 'forgot' && (
            <Tabs
              value={tab}
              onChange={(_, v) => switchTab(v)}
              variant="fullWidth"
              sx={{ mb: 3 }}
            >
              <Tab label="Entrar" value="login" />
              <Tab label="Primeiro acesso" value="register" />
            </Tabs>
          )}

          {tab === 'register' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Só é possível criar conta com um email previamente autorizado pelo
              administrador.
            </Alert>
          )}

          {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {tab === 'forgot' ? (
            forgotSent ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Se este email tiver uma conta cadastrada, enviamos um link para
                  redefinir a senha. Verifique sua caixa de entrada (e o spam).
                </Alert>
                <Button fullWidth onClick={() => switchTab('login')}>Voltar para o login</Button>
              </>
            ) : (
              <Box component="form" onSubmit={handleForgotSubmit} noValidate>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Informe seu email. Se houver uma conta cadastrada, enviaremos um link
                  para redefinir a senha.
                </Typography>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                  autoFocus
                  autoComplete="username"
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={busy || !email}
                  sx={{ mt: 1, mb: 1 }}
                >
                  {busy ? <CircularProgress size={24} /> : 'Enviar link de redefinição'}
                </Button>
                <Button fullWidth onClick={() => switchTab('login')} disabled={busy}>
                  Voltar para o login
                </Button>
              </Box>
            )
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                autoFocus
                autoComplete="username"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Senha"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
                helperText={tab === 'register' ? 'Mínimo de 8 caracteres.' : undefined}
                sx={{ mb: 1 }}
              />
              {tab === 'login' && (
                <Box sx={{ textAlign: 'right', mb: 2 }}>
                  <Button size="small" onClick={openForgotPassword} sx={{ textTransform: 'none' }}>
                    Esqueci minha senha
                  </Button>
                </Box>
              )}
              {tab === 'register' && (
                <TextField
                  label="Confirme a senha"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  fullWidth
                  required
                  autoComplete="new-password"
                  sx={{ mb: 2, mt: 1 }}
                />
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={busy || !email || !password}
                sx={{ mt: 1 }}
              >
                {busy ? <CircularProgress size={24} /> : tab === 'login' ? 'Entrar' : 'Criar conta'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
